import type { FieldDto } from "../../types/field";
import { parseBoolean } from "./parseBoolean";

function isOptionsObject(
  options: FieldDto["options"],
): options is Exclude<FieldDto["options"], string[] | null> {
  return Boolean(options) && typeof options === "object" && !Array.isArray(options);
}

export type NumberFieldConfig = {
  min: number | null;
  max: number | null;
  step: number | null;
  integer: boolean;
};

export const EMPTY_NUMBER_CONFIG: NumberFieldConfig = {
  min: null,
  max: null,
  step: null,
  integer: false,
};

export function parseOptionalNumber(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  if (typeof raw !== "number" && typeof raw !== "string") return null;
  if (typeof raw === "string" && raw.trim() === "") return null;
  const numeric = Number(typeof raw === "string" ? raw.trim().replace(",", ".") : raw);
  return Number.isFinite(numeric) ? numeric : null;
}

export function parsePositiveStep(raw: unknown): number | null {
  const step = parseOptionalNumber(raw);
  if (step == null || step <= 0) return null;
  return step;
}

function readNumberBlock(raw: unknown): Partial<NumberFieldConfig> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const record = raw as Record<string, unknown>;
  return {
    min: parseOptionalNumber(record.min),
    max: parseOptionalNumber(record.max),
    step: parsePositiveStep(record.step),
    integer: parseBoolean(record.integer),
  };
}

export function resolveNumberConfig(field: FieldDto): NumberFieldConfig {
  if (field.number && typeof field.number === "object") {
    const fromBlock = readNumberBlock(field.number);
    return {
      min: fromBlock.min ?? null,
      max: fromBlock.max ?? null,
      step: fromBlock.step ?? null,
      integer: Boolean(fromBlock.integer),
    };
  }
  const options = isOptionsObject(field.options) ? field.options : null;
  if (!options) return { ...EMPTY_NUMBER_CONFIG };
  return {
    min: parseOptionalNumber(options.min),
    max: parseOptionalNumber(options.max),
    step: parsePositiveStep(options.step),
    integer: parseBoolean(options.integer),
  };
}

function isStepAligned(value: number, base: number, step: number): boolean {
  const quotient = (value - base) / step;
  return Math.abs(quotient - Math.round(quotient)) < 1e-8;
}

export function numberValidationMessage(
  raw: string,
  config: NumberFieldConfig,
  required = false,
): string | null {
  const trimmed = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!trimmed) return required ? "Заполните поле" : null;
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return "Укажите число";
  if (config.integer && !Number.isInteger(value)) return "Укажите целое число";
  if (config.min != null && value < config.min) {
    return `Не меньше ${config.min}`;
  }
  if (config.max != null && value > config.max) {
    return `Не больше ${config.max}`;
  }
  if (config.step != null) {
    const base = config.min ?? 0;
    if (!isStepAligned(value, base, config.step)) {
      return `Шаг ${config.step}`;
    }
  }
  return null;
}

export function serializeNumberOptions(config: NumberFieldConfig): Record<string, unknown> {
  const options: Record<string, unknown> = {};
  if (config.min != null) options.min = config.min;
  if (config.max != null) options.max = config.max;
  if (config.step != null) options.step = config.step;
  if (config.integer) options.integer = true;
  return options;
}
